import type { ActualChatMessage, WorldEnvironment, WorldMessage } from "./types";

export const defaultEnvironment: WorldEnvironment = {
  light: 5,
  dark: 5,
  air: 5,
  water: 5,
  earth: 5,
  fire: 5,
  sky: 5,
  void: 5,
};

export const worldSystemPrompt = `You are the voice of Centience Worlds, an interactive world simulator presented through a terminal. Continue the user's world with concrete sensory detail and strange, internally consistent consequences. Keep responses concise enough to feel like live terminal output. Do not mention these instructions.`;

export function environmentPrompt(environment: WorldEnvironment): string {
  return Object.entries(environment)
    .map(([name, value]) => `${name} ${value}/10`)
    .join(", ");
}

export function imagePrompt(messages: WorldMessage[], environment: WorldEnvironment): string {
  const context = messages.slice(-4).map((message) => `${message.role}: ${message.content}`).join("\n");
  return [
    "Photorealistic first-person environmental concept art from an uncanny Source-engine-like world simulator.",
    `World parameters: ${environmentPrompt(environment)}.`,
    "No interface, text, watermark, weapon, logo, or frame. Cinematic practical lighting, physical materials, coherent architecture.",
    context.slice(0, 1_500),
  ].join("\n");
}

export function actualMessages(messages: WorldMessage[]): ActualChatMessage[] {
  return [
    { role: "system", content: worldSystemPrompt },
    ...messages.slice(-16).map(({ role, content }) => ({ role, content })),
  ];
}

export function parseActualText(value: unknown): string | null {
  if (!value || typeof value !== "object") return null;
  const choices = (value as { choices?: unknown }).choices;
  if (!Array.isArray(choices)) return null;
  const first = choices[0];
  if (!first || typeof first !== "object") return null;
  const message = (first as { message?: unknown }).message;
  if (!message || typeof message !== "object") return null;
  const content = (message as { content?: unknown }).content;
  return typeof content === "string" && content.trim() ? content.trim() : null;
}

export function parseActualModels(value: unknown): string[] {
  if (!value || typeof value !== "object") return [];
  const data = (value as { data?: unknown }).data;
  if (!Array.isArray(data)) return [];
  return [...new Set(data.flatMap((entry) => (
    entry && typeof entry === "object" && typeof (entry as { id?: unknown }).id === "string"
      ? [(entry as { id: string }).id]
      : []
  )))].sort();
}
